import React from "react";
import { useTranslation } from "react-i18next";
import { useSpaces } from "../../context/SpaceProvider";
import { Badge } from "../ui/badge";
import { X, Globe } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { cn } from "../../lib/utils";

interface SpaceTagPickerProps {
  selectedSpaceIds: string[];
  onChange: (spaceIds: string[]) => void;
  /** Compact mode for inline use in cards */
  compact?: boolean;
  className?: string;
}

/**
 * Multi-select space tag picker used in create/edit dialogs
 * and "Assign to Space" dropdowns.
 */
const SpaceTagPicker: React.FC<SpaceTagPickerProps> = ({
  selectedSpaceIds,
  onChange,
  compact = false,
  className,
}) => {
  const { t } = useTranslation();
  const { spaces } = useSpaces();

  if (!spaces || spaces.length === 0) return null;

  const toggleSpace = (spaceId: string) => {
    if (selectedSpaceIds.includes(spaceId)) {
      onChange(selectedSpaceIds.filter((id) => id !== spaceId));
    } else {
      onChange([...selectedSpaceIds, spaceId]);
    }
  };

  const removeSpace = (spaceId: string) => {
    onChange(selectedSpaceIds.filter((id) => id !== spaceId));
  };

  const selectedSpaces = spaces.filter((s) => selectedSpaceIds.includes(s._id));

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {/* Selected space badges */}
      {selectedSpaces.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedSpaces.map((space) => (
            <Badge
              key={space._id}
              variant="outline"
              className="text-xs py-0.5 px-2 gap-1.5 bg-white/80 hover:bg-white transition-colors cursor-default"
              style={{ borderColor: space.color + "60", color: space.color }}
            >
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: space.color }}
              />
              <span className="truncate max-w-[120px]">
                {space.name}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeSpace(space._id);
                }}
                className="ml-0.5 hover:bg-gray-100 rounded-full p-0.5 transition-colors"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Add space popover */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={cn(
              "inline-flex items-center gap-1.5 text-xs transition-colors rounded-md",
              compact
                ? "text-gray-400 hover:text-gray-600 py-0.5"
                : "text-primary/70 hover:text-primary hover:bg-primary/5 px-2 py-1.5 border border-dashed border-primary/20 hover:border-primary/40"
            )}
          >
            <Globe className="w-3 h-3" />
            {selectedSpaces.length === 0
              ? (t("assign_to_space") || "Assign to space")
              : (t("add_space") || "Add space")}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-1.5" align="start">
          <div className="text-xs font-medium text-gray-400 uppercase tracking-wider px-2 py-1.5 mb-1">
            {t("spaces") || "Spaces"}
          </div>
          {spaces.map((space) => {
            const isSelected = selectedSpaceIds.includes(space._id);
            return (
              <button
                key={space._id}
                onClick={() => toggleSpace(space._id)}
                className={cn(
                  "w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-sm transition-colors text-start",
                  isSelected
                    ? "bg-primary/5 text-gray-800"
                    : "text-gray-600 hover:bg-gray-50"
                )}
              >
                <div
                  className={cn(
                    "w-3 h-3 rounded-full flex-shrink-0 border-2 transition-colors",
                    isSelected ? "border-transparent" : "border-gray-200"
                  )}
                  style={
                    isSelected
                      ? { backgroundColor: space.color }
                      : { backgroundColor: space.color + "30" }
                  }
                />
                <span className="flex-1 truncate">
                  {space.name}
                </span>
                {isSelected && (
                  <div
                    className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[10px] flex-shrink-0"
                    style={{ backgroundColor: space.color }}
                  >
                    ✓
                  </div>
                )}
              </button>
            );
          })}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default SpaceTagPicker;
